import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../app/Auth';
import { Chip, ErrorNote, Icon, SectionTitle, Spinner } from '../app/ui';
import { callFn } from '../lib/api';
import { ageLabel, titleCase } from '../lib/format';
import { DONE_STATUSES, ISSUE_TYPES, SEVERITIES, STATUS_LABEL, STATUS_ORDER, statusText, tagColor, tagsOf, toggleTag } from '../lib/issues';
import { useApi } from '../lib/useApi';
import type { Issue, IssueDetail as Detail, IssueInstance, IssueTag } from '../lib/types';

export function TagChip({ name, palette }: { name: string; palette: IssueTag[] }) {
  const c = tagColor(name, palette);
  return <span className="tag-chip" style={{ color: c, borderColor: `${c}88`, background: `${c}26` }}>{name}</span>;
}

/** Helm-side tags + note + priority (nothing here is written to a tracker). */
function HelmOverlay({ instance, issue, palette, onTags, onNote, onClearRank, onDeleteTag }: {
  instance: IssueInstance; issue: Issue; palette: IssueTag[];
  onTags: (tags: string[]) => Promise<void>; onNote: (note: string) => Promise<void>;
  onClearRank: () => Promise<void>; onDeleteTag: (name: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [fresh, setFresh] = useState('');
  const [note, setNote] = useState(issue.helmNote ?? '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNote(issue.helmNote ?? ''); }, [issue.helmNote, issue.key]);
  const tags = tagsOf(issue);

  async function guard(fn: () => Promise<void>) { setBusy(true); try { await fn(); } finally { setBusy(false); } }
  const addFresh = (e: FormEvent) => {
    e.preventDefault();
    const n = fresh.trim().replace(/\s+/g, ' ');
    if (!n) return;
    setFresh('');
    if (!tags.some((t) => t.toLowerCase() === n.toLowerCase())) void guard(() => onTags([...tags, n]));
  };

  return (
    <div className="overlay">
      <SectionTitle>Helm tags, note and priority</SectionTitle>
      <p className="muted small">Stored in Helm only — {instance.paying ? 'AquaStar cannot see these' : 'nothing changes on the tracker'}.</p>
      <div className="row wrap gap">
        {tags.length ? tags.map((t) => <TagChip key={t} name={t} palette={palette} />) : <span className="muted small">No tags</span>}
        <button className="btn small" onClick={() => setEditing(!editing)}>{editing ? 'Done' : 'Edit tags'}</button>
      </div>
      {editing ? (
        <div className="tag-editor">
          <div className="row wrap gap">
            {palette.map((p) => {
              const on = tags.some((t) => t.toLowerCase() === p.name.toLowerCase());
              return (
                <span key={p.name} className="tag-toggle">
                  <button type="button" className={`tag-pick${on ? ' on' : ''}`} disabled={busy} aria-pressed={on}
                    onClick={() => void guard(() => onTags(toggleTag(tags, p.name)))}>
                    <TagChip name={p.name} palette={palette} />{on ? <Icon name="check" size={14} /> : null}
                  </button>
                  <button type="button" className="icon-btn tiny" aria-label={`Delete tag ${p.name} everywhere`} disabled={busy}
                    onClick={() => { if (window.confirm(`Delete the tag “${p.name}” from every issue?`)) void guard(() => onDeleteTag(p.name)); }}>
                    <Icon name="x" size={12} />
                  </button>
                </span>
              );
            })}
          </div>
          <form className="row gap" onSubmit={addFresh}>
            <input value={fresh} onChange={(e) => setFresh(e.target.value)} placeholder="New tag" maxLength={40} />
            <button className="btn small" type="submit" disabled={busy || !fresh.trim()}>Add</button>
          </form>
        </div>
      ) : null}
      <label className="small muted" htmlFor="helm-note">Private note</label>
      <textarea id="helm-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
      <div className="row gap">
        <button className="btn small" disabled={busy || note === (issue.helmNote ?? '')} onClick={() => void guard(() => onNote(note))}>Save note</button>
        {issue.helmRank != null ? (
          <>
            <Chip tone="accent">Helm priority #{issue.helmRank + 1}</Chip>
            <button className="btn small" disabled={busy} onClick={() => void guard(onClearRank)}>Remove from Helm order</button>
          </>
        ) : <span className="muted small">Not in Helm's priority order</span>}
      </div>
    </div>
  );
}

export function IssueDrawer({ instance, issue, palette, onClose, onChanged, onTags, onNote, onClearRank, onDeleteTag }: {
  instance: IssueInstance; issue: Issue; palette: IssueTag[]; onClose: () => void; onChanged: () => void;
  onTags: (tags: string[]) => Promise<void>; onNote: (note: string) => Promise<void>;
  onClearRank: () => Promise<void>; onDeleteTag: (name: string) => Promise<void>;
}) {
  const { toast, me } = useAuth();
  const aquastar = instance.id === 'aquastar-dev';
  const q = useApi<Detail>(aquastar ? 'aq_issue' : 'issue_detail', aquastar ? { id: issue.id } : { instance: instance.id, app: issue.app ?? '', id: issue.id });
  const d = q.data;
  const [draft, setDraft] = useState('');
  const [pub, setPub] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const writer = me?.role === 'owner' || me?.role === 'member';
  const current = d?.issue.status ?? issue.status ?? 'new';

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function post() {
    if (pub && !window.confirm('Post this comment where AquaStar can read it?')) return;
    setBusy(true); setErr(null);
    try { await callFn('aq_comment', { id: issue.id, body: draft, public: pub }); setDraft(''); setPub(false); await q.reload(true); toast('Comment posted', 'ok'); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function move(to: string) {
    if (to === current) return;
    setBusy(true); setErr(null);
    try {
      const r = await callFn<{ verified?: boolean }>('aq_transition', { id: issue.id, toStatus: to, note: note.trim() || undefined });
      setNote(''); await q.reload(true); onChanged();
      if (r.verified === false) setErr('Sent; the tracker did not confirm the new status yet — check the admin console.');
      else toast(`Moved to ${STATUS_LABEL[to] ?? to}`, 'ok');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <div className="drawer-wrap" role="dialog" aria-modal="true" aria-label={`Issue ${issue.key}`}>
      <button className="scrim" aria-label="Close" onClick={onClose} />
      <aside className="drawer">
        <header className="drawer-head">
          <span className="mono key">{issue.key}</span>
          {issue.app ? <Chip>{issue.app}</Chip> : null}
          <span className="spacer" />
          {issue.url ? <a className="btn small" href={issue.url} target="_blank" rel="noopener noreferrer">Admin console ↗</a> : null}
          <button className="icon-btn" onClick={onClose} aria-label="Close"><Icon name="x" /></button>
        </header>
        <div className="drawer-body">
          <h2>{issue.title}</h2>
          <div className="row wrap gap">
            <Chip tone="accent">{statusText(d?.issue ?? issue)}</Chip>
            {issue.severity ? <Chip tone={issue.severity === 'critical' ? 'red' : issue.severity === 'high' ? 'orange' : 'neutral'}>{titleCase(issue.severity)}</Chip> : null}
            <span className="muted small">{issue.typeName ?? titleCase(issue.type)}</span>
            <span className="muted small">opened {ageLabel(issue.createdAt ?? issue.updatedAt)} ago</span>
          </div>
          {aquastar && writer ? (
            <div className="row wrap gap">
              <label className="small muted" htmlFor="st">Status</label>
              <select id="st" value={current} disabled={busy} onChange={(e) => void move(e.target.value)}>
                {[...STATUS_ORDER, ...DONE_STATUSES.filter((s) => s !== 'superseded')].map((s) => <option key={s} value={s}>{STATUS_LABEL[s] ?? s}</option>)}
              </select>
              <input className="grow" placeholder="Note for the status change (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          ) : null}
          <ErrorNote text={err ?? q.error} />
          {q.loading && !d ? <Spinner label="Loading…" /> : null}
          {d?.issue.description ? (<><SectionTitle>Description</SectionTitle><p className="pre">{d.issue.description}</p></>) : null}
          {d ? (
            <>
              <SectionTitle count={d.comments.length}>Comments</SectionTitle>
              {d.comments.map((c) => (
                <div key={c.id} className="comment">
                  <div className="row gap small">
                    <strong>{c.author ?? '—'}</strong>
                    <Chip tone={c.visibility === 'internal' ? 'neutral' : 'orange'}>{c.visibility === 'internal' ? 'Internal' : aquastar ? 'Visible to AquaStar' : 'Public'}</Chip>
                    <span className="spacer" /><span className="muted">{ageLabel(c.createdAt)}</span>
                  </div>
                  <p className="pre">{c.body}</p>
                </div>
              ))}
            </>
          ) : null}
          <HelmOverlay instance={instance} issue={issue} palette={palette} onTags={onTags} onNote={onNote} onClearRank={onClearRank} onDeleteTag={onDeleteTag} />
          {aquastar && writer ? (
            <div className="add-comment">
              <SectionTitle>Add a comment</SectionTitle>
              <textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Comment" />
              <label className="check"><input type="checkbox" checked={pub} onChange={(e) => setPub(e.target.checked)} /> Visible to AquaStar</label>
              <p className={`small ${pub ? 'warn' : 'muted'}`}>{pub ? 'AquaStar (Jim) will see this comment.' : 'Internal: only K-Stack staff see it.'}</p>
              <button className="btn primary" disabled={busy || !draft.trim()} onClick={() => void post()}>{pub ? 'Post publicly…' : 'Post internal comment'}</button>
            </div>
          ) : null}
          {!aquastar ? <p className="muted small">Read-only here — change this issue in the admin console.</p> : null}
        </div>
      </aside>
    </div>
  );
}

export function NewIssueDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (key: string | null) => void }) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [type, setType] = useState('bug');
  const [sev, setSev] = useState('medium');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      const r = await callFn<{ issue: { key?: string | null } }>('aq_create', { title: title.trim(), description: desc, typeKey: type, severityKey: sev });
      onCreated(r.issue?.key ?? null);
    } catch (x) { setErr(x instanceof Error ? x.message : String(x)); setBusy(false); }
  }
  return (
    <div className="drawer-wrap center" role="dialog" aria-modal="true" aria-label="New AquaStar issue">
      <button className="scrim" aria-label="Close" onClick={onClose} />
      <form className="dialog card" onSubmit={submit}>
        <h2>New AquaStar issue</h2>
        <label htmlFor="ni-t">Title</label>
        <input id="ni-t" value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        <label htmlFor="ni-d">Description</label>
        <textarea id="ni-d" rows={5} value={desc} onChange={(e) => setDesc(e.target.value)} />
        <div className="row gap">
          <div className="grow"><label htmlFor="ni-ty">Type</label>
            <select id="ni-ty" value={type} onChange={(e) => setType(e.target.value)}>{ISSUE_TYPES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}</select></div>
          <div className="grow"><label htmlFor="ni-s">Severity</label>
            <select id="ni-s" value={sev} onChange={(e) => setSev(e.target.value)}>{SEVERITIES.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}</select></div>
        </div>
        <p className="muted small">Filed on AquaStar's dev tracker. AquaStar can see issues on their tracker.</p>
        <ErrorNote text={err} />
        <div className="row gap end">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" type="submit" disabled={busy || !title.trim()}>{busy ? 'Filing…' : 'File'}</button>
        </div>
      </form>
    </div>
  );
}
